import React, { memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useTheme } from '@/context/ThemeContext';
import { getBidiTextProps, ltrIsolateStyle } from '@/ai-platform/components/chat/textDirection';

/**
 * MarkdownRenderer - Renders markdown content with syntax highlighting
 * 
 * Features:
 * - GitHub Flavored Markdown (tables, strikethrough, task lists, etc.)
 * - Syntax highlighting for code blocks
 * - Theme-aware (dark/light mode support)
 * - Copy button for code blocks
 */
const MarkdownRenderer = memo(({ content, className = '' }) => {
  const { isDark } = useTheme();
  const bidiProps = getBidiTextProps(content);
  
  // Custom code block component with syntax highlighting
  // eslint-disable-next-line no-unused-vars
  const CodeBlock = ({ node, inline, className: codeClassName, children, ...props }) => {
    const match = /language-(\w+)/.exec(codeClassName || '');
    const language = match ? match[1] : '';
    const codeString = String(children).replace(/\n$/, '');
    
    // Handle inline code
    if (inline) {
      return (
        <code
          dir="ltr"
          style={{
            ...ltrIsolateStyle,
            backgroundColor: isDark ? '#374151' : '#f3f4f6',
            color: isDark ? '#f9fafb' : '#1f2937',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '0.875em',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          }}
          {...props}
        >
          {children}
        </code>
      );
    }
    
    // Handle code blocks with syntax highlighting
    return (
      <div dir="ltr" style={{ ...ltrIsolateStyle, position: 'relative', margin: '8px 0' }}>
        {language && (
          <div
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              fontSize: '11px',
              color: isDark ? '#9ca3af' : '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {language}
          </div>
        )}
        <SyntaxHighlighter
          style={isDark ? oneDark : oneLight}
          language={language || 'text'}
          PreTag="div"
          customStyle={{
            margin: 0,
            borderRadius: '8px',
            fontSize: '13px',
            padding: '16px',
          }}
          {...props}
        >
          {codeString}
        </SyntaxHighlighter>
      </div>
    );
  };
  
  // Custom components for markdown elements
  const components = {
    code: CodeBlock,
    // Style tables
    table: ({ children }) => (
      <div style={{ overflowX: 'auto', margin: '8px 0' }}>
        <table
          style={{
            borderCollapse: 'collapse',
            width: '100%',
            fontSize: '14px',
          }}
        >
          {children}
        </table>
      </div>
    ),
    th: ({ children }) => (
      <th
        style={{
          border: `1px solid ${isDark ? '#4b5563' : '#d1d5db'}`,
          padding: '8px 12px',
          backgroundColor: isDark ? '#374151' : '#f3f4f6',
          textAlign: 'start',
          fontWeight: 600,
        }}
      >
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td
        style={{
          border: `1px solid ${isDark ? '#4b5563' : '#d1d5db'}`,
          padding: '8px 12px',
        }}
      >
        {children}
      </td>
    ),
    // Style blockquotes
    blockquote: ({ children }) => (
      <blockquote
        style={{
          borderInlineStart: `4px solid ${isDark ? '#6b7280' : '#d1d5db'}`,
          margin: '8px 0',
          paddingInlineStart: '16px',
          color: isDark ? '#9ca3af' : '#6b7280',
          fontStyle: 'italic',
        }}
      >
        {children}
      </blockquote>
    ),
    // Style links
    a: ({ href, children }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: isDark ? '#60a5fa' : '#2563eb',
          textDecoration: 'underline',
        }}
      >
        {children}
      </a>
    ),
    // Style lists
    ul: ({ children }) => (
      <ul style={{ margin: '8px 0', paddingInlineStart: '24px' }}>{children}</ul>
    ),
    ol: ({ children }) => (
      <ol style={{ margin: '8px 0', paddingInlineStart: '24px' }}>{children}</ol>
    ),
    li: ({ children }) => (
      <li style={{ margin: '4px 0' }}>{children}</li>
    ),
    // Style headings
    h1: ({ children }) => (
      <h1 style={{ fontSize: '1.5em', fontWeight: 700, margin: '16px 0 8px' }}>{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 style={{ fontSize: '1.3em', fontWeight: 600, margin: '14px 0 6px' }}>{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 style={{ fontSize: '1.1em', fontWeight: 600, margin: '12px 0 4px' }}>{children}</h3>
    ),
    // Style paragraphs
    p: ({ children }) => (
      <p style={{ margin: '8px 0', lineHeight: 1.6 }}>{children}</p>
    ),
    // Style horizontal rules
    hr: () => (
      <hr
        style={{
          border: 'none',
          borderTop: `1px solid ${isDark ? '#4b5563' : '#d1d5db'}`,
          margin: '16px 0',
        }}
      />
    ),
  };
  
  return (
    <div className={`markdown-content ${className}`} {...bidiProps}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
});

MarkdownRenderer.displayName = 'MarkdownRenderer';

export default MarkdownRenderer;
